import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import PayoutProgressMeter from "@/components/PayoutProgressMeter";
import { BankIcon as Bank, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

/**
 * Stripe Connect status banner for the transporter dashboard.
 *
 * Mirrors `<StripeConnectBanner/>` (used by breeders/providers/storefronts)
 * but reads from the transporter-specific endpoints because transporters
 * carry their `stripe_connect_account_id` on the `transporters` row, not
 * the shared `connected_accounts` collection.
 *
 * Three states:
 *  - Account exists + payouts_enabled       → quiet green strip + "Manage payouts"
 *  - Account exists but onboarding pending  → amber "Finish onboarding" card with resume CTA
 *  - No Stripe account on file (unusual)    → red "Contact admin" card
 *
 * Backend contract:
 *   GET  /api/me/transporter/payouts/status            → {ready, needs_onboarding, account|null}
 *   POST /api/me/transporter/payouts/onboarding-link   → {url}
 *   POST /api/me/transporter/payouts/dashboard-link    → {url}
 */
export default function TransporterPayoutsBanner({ asEmail = "" }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const asParam = asEmail ? `?as_email=${encodeURIComponent(asEmail)}` : "";

  useEffect(() => {
    api
      .get(`/me/transporter/payouts/status${asParam}`, { silent: true })
      .then((r) => setStatus(r.data || {}))
      .catch(() => setStatus({}));
  }, [asParam]);

  if (status === null) return null;

  const acct = status.account || {};
  const hasAccount = !!acct.id;
  const ready = !!status.ready;
  const submitted = !!acct.details_submitted;
  const due = acct.requirements_currently_due || [];

  // READY — quiet success strip
  if (ready) {
    return (
      <div
        className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-[#BFE8C9] bg-[#EEFBEC] px-4 py-3"
        data-testid="transporter-payouts-banner-ready"
      >
        <div className="flex items-center gap-2 text-[13px] text-[#0F7B3E]">
          <CheckCircle size={18} weight="fill" />
          <span>
            <strong>Payouts ready.</strong> Trip earnings auto-release to your bank account 48 hours
            after each delivery is confirmed.
          </span>
        </div>
        <button
          onClick={async () => {
            setBusy(true);
            try {
              const { data } = await api.post(
                `/me/transporter/payouts/dashboard-link${asParam}`,
              );
              if (data?.url) window.open(data.url, "_blank", "noopener");
            } catch (e) {
              toast.error(formatApiError(e));
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
          data-testid="transporter-payouts-manage-link"
          className="text-[12px] font-bold text-ink hover:text-crimson underline whitespace-nowrap"
        >
          Manage payouts ↗
        </button>
      </div>
    );
  }

  // NO ACCOUNT — unusual; admin failed to create one
  if (!hasAccount) {
    return (
      <div
        className="mb-6 rounded-xl border-2 border-crimson/40 bg-[#FFF0F0] p-4 sm:p-5 flex items-start gap-3"
        data-testid="transporter-payouts-banner-no-account"
      >
        <span className="p-2 rounded-full bg-[#FDEEEE]">
          <Warning size={20} weight="fill" className="text-crimson" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-spectral text-[17px] sm:text-lg leading-tight">
            No Stripe Connect account on file
          </h3>
          <p className="text-[12.5px] sm:text-[13px] text-[#3F3F3F] mt-1 leading-snug">
            This is unusual — your approval should have created one. Email{" "}
            <a href="mailto:admin@armstrongk9registry.com" className="underline font-semibold">
              admin@armstrongk9registry.com
            </a>{" "}
            so we can re-issue it. Until this is resolved you can't be assigned paid trips.
          </p>
        </div>
      </div>
    );
  }

  // EXISTS BUT NOT READY — resume onboarding
  const onResume = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/me/transporter/payouts/onboarding-link${asParam}`,
      );
      if (data?.url) window.location.href = data.url;
      else throw new Error("Stripe onboarding link unavailable.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`mb-6 rounded-xl border-2 p-4 sm:p-5 flex items-start gap-3 ${
        submitted
          ? "border-[#E8D9A6] bg-[#FBF7EC]"
          : "border-crimson/40 bg-[#FFF0F0]"
      }`}
      data-testid={submitted ? "transporter-payouts-banner-progress" : "transporter-payouts-banner-missing"}
    >
      <span className={`p-2 rounded-full ${submitted ? "bg-[#FBF1D2]" : "bg-[#FDEEEE]"}`}>
        {submitted ? (
          <Warning size={20} weight="fill" className="text-[#8A6A00]" />
        ) : (
          <Bank size={20} weight="duotone" className="text-crimson" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <h3 className="font-spectral text-[17px] sm:text-lg leading-tight">
          {submitted
            ? "Finish payouts setup"
            : "You can't receive trip payouts yet — finish Stripe setup"}
        </h3>
        <p className="text-[12.5px] sm:text-[13px] text-[#3F3F3F] mt-1 leading-snug">
          {submitted
            ? "Stripe is reviewing the rest of your info. Most accounts clear in 24 hours. Until then, you can browse bid invitations but won't be assigned new paid trips."
            : "Trip earnings flow through Stripe Connect — a 3-minute setup that links your bank account so Armstrong can pay you 90% of each fare directly. You can pick this up where you left off."}
        </p>
        <PayoutProgressMeter progress={acct.setup_progress} />
        {due.length > 0 && (
          <p className="mt-2 text-[11.5px] text-[#8A4A00]">
            <strong>Still needed:</strong>{" "}
            {(acct.requirements_currently_due_labels || due).slice(0, 3).join(", ")}
            {(acct.requirements_currently_due_labels || due).length > 3 ? "…" : ""}
          </p>
        )}
        {!submitted && (
          <p className="mt-2 text-[11px] text-[#6B6B6B] leading-snug">
            Takes about 5 minutes. Have handy: a photo ID, your bank routing
            &amp; account numbers, and your SSN (or EIN for a business).
          </p>
        )}
        <Button
          onClick={onResume}
          disabled={busy}
          data-testid="transporter-payouts-start-btn"
          className="mt-3 bg-ink hover:bg-crimson text-white rounded-full h-10 px-5"
        >
          {submitted ? "Continue setup" : "Resume Stripe setup"}{" "}
          <ArrowRight size={13} weight="bold" className="ml-1" />
        </Button>
      </div>
    </div>
  );
}
